
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { NextSeo, NextSeoProps } from 'next-seo';
import { GetStaticPropsContext } from 'next/types';
import { Page as MakeswiftPage, PageProps as MakeswiftPageProps } from '@makeswift/runtime/next';
import { SWRConfig } from 'swr';
import { CaseStudyFeedDocument, CaseStudyPropertyDocument } from '@/generated/contentful';
import { client as contentful } from '@/lib/contentful/client';
import { serializeCacheKey } from '@/lib/contentful/utils';
import { getPageSnapshot } from '@/lib/makeswift/client';
import '@/lib/makeswift/components';
import { runtime } from '@/lib/makeswift/runtime';
import getLocalizedPathFromEnglish from '@/utils/locales/localized-routes/getLocalizedPathFromEnglish';
import { pageLocales } from '@/utils/locales/page-locales';
import { getPageLanguageAlternatives } from '@/utils/makeswift/getPageLanguageAlternatives';
import { generateSeoUrl } from '@/utils/seo/generateSeoUrl';
import { getNoIndexValueFromMakeSwift } from '@/utils/seo/getNoIndexValueFromMakeSwift';
import { useUrl } from '@/utils/url/useUrl';
import { DEFAULT_LIMIT } from '@/components/Contentful/entries/CaseStudy/CaseStudyFeed/CaseStudyFeed';
import { PreviewToast } from '@/components/PreviewToast';
type Props = {
    languageAlternates: NextSeoProps['languageAlternates'];
    fallback: {
        [key: string]: unknown;
    };
    preview: boolean;
} & MakeswiftPageProps;
export default function Page({ snapshot, fallback, preview, languageAlternates }: Props) {
    const url = useUrl();
    const noIndex = getNoIndexValueFromMakeSwift({ snapshot, url });
    return (<SWRConfig value={{ fallback }}>
			<NextSeo canonical={generateSeoUrl(url?.href)} openGraph={{
            url: generateSeoUrl(url?.href)
        }} languageAlternates={languageAlternates} noindex={noIndex}/>

			<PreviewToast preview={preview}/>
			<MakeswiftPage snapshot={snapshot} runtime={runtime}/>
		</SWRConfig>);
}
async function getStaticProps({ previewData, preview, locale }: GetStaticPropsContext<{
    slug: string;
}, {
    makeswift: boolean;
}>) {
    if (!preview && !pageLocales['case-study']?.includes(locale as Locale)) {
        console.warn('[Case Study Index 404] - Locale not supported for /case-study/');
        return { notFound: true, revalidate: 3600 };
    }
    const client = contentful({ preview: Boolean(preview) });
    const caseStudyFilters = await client.request(CaseStudyPropertyDocument, {
        locale,
        preview: Boolean(preview)
    });
    const caseStudyFeed = await client.request(CaseStudyFeedDocument, {
        limit: 12,
        skip: 0,
        locale,
        preview: Boolean(preview),
        where: {
            status: true
        }
    });
    if (!caseStudyFeed?.caseStudyCollection?.items.length) {
        console.warn('[Case Study Index 404] - No Case Studies found for /case-study/');
        return { notFound: true, revalidate: 3600 };
    }
    const snapshot = await getPageSnapshot({
        pathname: '/case-study',
        isMakeswiftPreview: previewData?.makeswift == true,
        previewData,
        locale,
        timeout: {
            ms: 10000,
            message: `Timeout request getPageSnapshot for "/case-study" – Locale: ${locale}`
        }
    });
    if (snapshot == null) {
        console.warn('[Case Study Index 404] - No makeswift snapshot found for /case-study/');
        return { notFound: true };
    }
    const languageAlternates = await getPageLanguageAlternatives({
        path: getLocalizedPathFromEnglish(locale, 'case-study'),
        locale,
        previewData
    });
    return {
        props: {
            snapshot,
            languageAlternates,
            fallback: {
                [serializeCacheKey('caseStudyFilters', {
                    locale
                })]: caseStudyFilters,
                [serializeCacheKey('caseStudyFeedTotal', {
                    locale,
                    industry: undefined,
                    topics: [],
                    competitors: []
                })]: caseStudyFilters,
                [serializeCacheKey('caseStudyFeed', {
                    limit: DEFAULT_LIMIT,
                    skip: 0,
                    locale,
                    industry: undefined,
                    topics: [],
                    competitors: []
                })]: caseStudyFeed
            },
            previewData: previewData?.makeswift == true,
            preview: Boolean(preview)
        },
        revalidate: 3600
    };
}

    async function __Next_Translate__getStaticProps__1934a36ec50__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/case-study/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1934a36ec50__ as getStaticProps }
  